// ABOUTME: The beats of 1A's first-arrival opening: the sun scene, the storm sky coming in at 1.6 s, the sun fading and the ring arriving at 4 s.
// ABOUTME: Decides once at mount whether to play; the screen can cancel only the ring (a press that leaves the ring behind) or skip to the settled state (a failed save).
import { useCallback, useEffect, useRef, useState } from 'react';

const STORM_IN_MS = 1600; // the storm layer starts to come in
const RING_IN_MS = 4000; // the sun layer starts to fade and the ring arrives

type OpeningSky = 'sun' | 'storm' | 'done';
type OpeningRing = 'waiting' | 'arriving' | 'up';

interface FirstArrivalInput {
  tooMuchCategories: readonly unknown[];
  breathSecondsTotal: number;
  breathRoundCount: number;
  progress: { canGoForward: boolean };
}

// A first arrival has nothing stored from this screen and nothing ahead on the path.
export function isFirstArrival(crossing: FirstArrivalInput): boolean {
  return crossing.tooMuchCategories.length === 0
    && crossing.breathSecondsTotal === 0
    && crossing.breathRoundCount === 0
    && !crossing.progress.canGoForward;
}

export function useOpeningSequence(firstArrival: boolean) {
  const [playing, setPlaying] = useState(firstArrival);
  const [beat, setBeat] = useState<0 | 1 | 2>(0);
  const [ring, setRing] = useState<OpeningRing>(firstArrival ? 'waiting' : 'up');
  const [ringKey, setRingKey] = useState(0);
  // Refs kept in step with the state above, so skip is stable and never reads a stale value.
  const playingRef = useRef(firstArrival);
  const ringRef = useRef<OpeningRing>(firstArrival ? 'waiting' : 'up');
  const ringCancelledRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const updateRing = useCallback((next: OpeningRing) => {
    ringRef.current = next;
    setRing(next);
  }, []);

  useEffect(() => {
    if (!playingRef.current) return undefined;
    timersRef.current = [
      setTimeout(() => setBeat(1), STORM_IN_MS),
      setTimeout(() => {
        setBeat(2);
        if (!ringCancelledRef.current) updateRing('arriving');
      }, RING_IN_MS),
    ];
    return clearTimers;
  }, [clearTimers, updateRing]);

  const cancelRing = useCallback(() => {
    ringCancelledRef.current = true;
  }, []);

  const skip = useCallback(() => {
    if (!playingRef.current && ringRef.current !== 'waiting') return;
    clearTimers();
    playingRef.current = false;
    setPlaying(false);
    updateRing('up');
    setRingKey(key => key + 1);
  }, [clearTimers, updateRing]);

  const sky: OpeningSky | undefined = playing ? (['sun', 'storm', 'done'] as const)[beat] : undefined;
  return { sky, ring, ringKey, cancelRing, skip };
}
